import { combineReducers, configureStore, createAction } from '@reduxjs/toolkit'
import { useDispatch, useSelector, type TypedUseSelectorHook } from 'react-redux'
import aiChat from './slices/aiChatSlice'
import cover from './slices/coverSlice'
import currentUser from './slices/currentUserSlice'
import folders from './slices/folderSlice'
import formAIGeneration from './slices/formAIGenerationSlice'
import formBillingAccounts from './slices/formBillingAccountSlice'
import formCooperators from './slices/formCooperatorSlice'
import formInviteLinks from './slices/formInviteLinkSlice'
import formRoles from './slices/formRoleSlice'
import formSettings from './slices/formSettingSlice'
import forms from './slices/formSlice'
import notifications from './slices/notificationSlice'
import onboarding from './slices/onboardingSlice'
import participatedFormCovers from './slices/participatedFormCoversSlice'
import createFormSheet from './slices/createFormSheetSlice'
import publishSheet from './slices/publishSheetSlice'
import search from './slices/searchSlice'
import session from './slices/sessionSlice'
import subAccounts from './slices/subAccountSlice'
import tableCover from './slices/tableCoverSlice'
import templates from './slices/templateSlice'
import trashedForms from './slices/trashedFormSlice'
import usageAndBalance from './slices/usageAndBalanceSlice'
import userFormSettings from './slices/userFormSettingSlice'

const combinedReducer = combineReducers({
  session,
  currentUser,
  cover,
  tableCover,
  createFormSheet,
  folders,
  forms,
  formBillingAccounts,
  formSettings,
  formCooperators,
  formInviteLinks,
  formRoles,
  userFormSettings,
  publishSheet,
  search,
  notifications,
  onboarding,
  usageAndBalance,
  subAccounts,
  aiChat,
  formAIGeneration,
  trashedForms,
  templates,
  participatedFormCovers,
})

export const resetStore = createAction('app/resetStore')

const rootReducerWithReset = (state: ReturnType<typeof combinedReducer> | undefined, action: any) => {
  if (action.type === resetStore.type) {
    const currentSession = state?.session
    const resetState = combinedReducer(undefined, { type: '@@INIT' })
    if (currentSession) {
      resetState.session = currentSession
    }
    return resetState
  }
  return combinedReducer(state, action)
}

export const store = configureStore({
  reducer: rootReducerWithReset,
  devTools: false,
  enhancers: getDefaultEnhancers => {
    if (__DEV__) {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const devToolsEnhancer = require('redux-devtools-expo-dev-plugin').default

      return getDefaultEnhancers().concat(
        devToolsEnhancer({
          trace: true,
        })
      )
    }

    return getDefaultEnhancers()
  },
})

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch

export const useAppDispatch: () => AppDispatch = useDispatch
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
